import { useEffect, useRef, useState } from 'react';
import { TitledCircularProgress } from '../components/Feedback/TitledCircularProgress/TitledCircularProgress';
import { sso } from './sso';

/**
 * react-router v6 reimplementation of the auth-reference `_authenticated` beforeLoad
 * guard. Requires a live internal-SSO session before rendering the authenticated tree;
 * a sessionless visit is sent to the Connected-App authorize URL as a full-document
 * redirect, with the page they asked for stashed through the OIDC `state` round-trip so
 * `/oauth/callback` can return them here.
 *
 * The check runs once per mount — this element wraps the whole authenticated `/*` route,
 * which stays mounted across in-app navigation, so it does not re-fire on every page
 * change; a full reload re-runs it. Under the DEV bypass sso.checkAuthenticated resolves
 * authenticated with no real session, so local dev renders without a sign-in round-trip.
 */
export function RequireAuth({ children }) {
  const [authed, setAuthed] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    sso
      .checkAuthenticated(window.location.href)
      .then((result) => {
        if (result.authenticated) {
          setAuthed(true);
        } else {
          // External, full-document redirect to the auth site (forces a reload).
          window.location.assign(result.redirectUrl);
        }
      })
      .catch(() => {
        window.location.assign('/Error/LoginFailed');
      });
  }, []);

  if (!authed) {
    return <TitledCircularProgress title="Signing in…" />;
  }
  return children;
}
